import { useEffect, useState } from 'react'

import { t } from '../../../common/i18n.js'
import type { Language } from '../../../common/types/i18n.js'
import { portalGet } from '../../views/portal/utils/api.js'
import { Button } from '../button/button.js'
import { BaseModal } from '../modal/base-modal/base-modal.js'
import {
    CalendarGateStatus,
    calendarNeedsConnect,
    HarvestGateStatus,
    harvestNeedsConnect,
    INTEGRATIONS_CHANGED_EVENT,
    INTEGRATIONS_SKIP_KEY,
    shouldShowIntegrationsGate,
} from './integrations-gate-logic.js'
import './integrations-gate.css'

/**
 * Post-login step prompting the user to connect their personal integrations
 * (Harvest, Google Calendar) — colleagues depend on this data for meeting
 * planning and capacity checks, so it nags until connected. Skippable per
 * browser session ("Jäta praegu vahele"), so a broken integration never locks
 * anyone out. Hidden mid-OAuth (?code= round trips) and in environments
 * without OAuth credentials.
 */
export const IntegrationsGate = ({ lang }: { lang: Language }) => {
    const ob = t.portal.onboarding

    const [harvest, setHarvest] = useState<HarvestGateStatus | null>(null)
    const [calendar, setCalendar] = useState<CalendarGateStatus | null>(null)
    const [dismissed, setDismissed] = useState(
        () => sessionStorage.getItem(INTEGRATIONS_SKIP_KEY) === '1',
    )
    // Bumped by INTEGRATIONS_CHANGED_EVENT to re-read status after a connect.
    const [refreshKey, setRefreshKey] = useState(0)

    useEffect(() => {
        const onChanged = () => setRefreshKey((k) => k + 1)
        window.addEventListener(INTEGRATIONS_CHANGED_EVENT, onChanged)
        return () => window.removeEventListener(INTEGRATIONS_CHANGED_EVENT, onChanged)
    }, [])

    useEffect(() => {
        if (dismissed) return
        // Mid-OAuth (?code=) the token isn't saved yet — reading now would cache a
        // stale "not connected" and re-show the gate once the code clears. Wait for
        // the INTEGRATIONS_CHANGED_EVENT fired after the exchange instead.
        if (new URLSearchParams(window.location.search).has('code')) return
        let cancelled = false
        void (async () => {
            const [gateRes, harvestRes, calendarRes] = await Promise.all([
                portalGet<{ dismissed: boolean }>('/api/portal/me/integrations-gate'),
                portalGet<HarvestGateStatus>('/api/portal/me/harvest'),
                portalGet<CalendarGateStatus>('/api/portal/calendar/status'),
            ])
            if (cancelled) return
            // An admin has hidden the gate for this user — never render it,
            // regardless of connection status.
            if (gateRes.ok && gateRes.data.dismissed) {
                setDismissed(true)
                return
            }
            if (harvestRes.ok) setHarvest(harvestRes.data)
            if (calendarRes.ok) setCalendar(calendarRes.data)
        })()
        return () => {
            cancelled = true
        }
    }, [dismissed, refreshKey])

    // Mid-OAuth round trip (?code=…) — let the exchange land before judging.
    const oauthInFlight = new URLSearchParams(window.location.search).has('code')

    // Skip for this browser session only — a temporarily broken integration
    // shouldn't lock anyone out, but the gate returns next session.
    const skip = () => {
        sessionStorage.setItem(INTEGRATIONS_SKIP_KEY, '1')
        setDismissed(true)
    }

    if (oauthInFlight || !shouldShowIntegrationsGate(harvest, calendar, dismissed)) {
        return null
    }

    const rows: {
        key: string
        title: string
        description: string
        needsConnect: boolean
        connectUrl: string | null
    }[] = [
        {
            key: 'harvest',
            title: ob.harvestTitle(lang),
            description: ob.harvestDescription(lang),
            needsConnect: harvest !== null && harvestNeedsConnect(harvest),
            connectUrl: harvest?.authUrl ?? null,
        },
        {
            key: 'calendar',
            title: ob.calendarTitle(lang),
            description: ob.calendarDescription(lang),
            needsConnect: calendar !== null && calendarNeedsConnect(calendar),
            connectUrl: calendar?.connectUrl ?? null,
        },
    ]

    return (
        <BaseModal isOpen onClose={skip} className="integrations-gate">
            <h3 className="integrations-gate__title">{ob.title(lang)}</h3>
            <p className="integrations-gate__description">{ob.description(lang)}</p>

            <div className="integrations-gate__rows">
                {rows.map((row) => (
                    <div key={row.key} className="integrations-gate__row">
                        <div className="integrations-gate__row-text">
                            <span className="integrations-gate__row-title">{row.title}</span>
                            <span className="integrations-gate__row-description">
                                {row.description}
                            </span>
                        </div>
                        {row.needsConnect && row.connectUrl ? (
                            <Button
                                variant="primary"
                                size="small"
                                text={ob.connect(lang)}
                                onClick={() => {
                                    window.location.href = row.connectUrl!
                                }}
                            />
                        ) : (
                            <span className="integrations-gate__connected">
                                {ob.connected(lang)}
                            </span>
                        )}
                    </div>
                ))}
            </div>

            <div className="integrations-gate__footer">
                <span className="integrations-gate__note">{ob.note(lang)}</span>
                <Button variant="tertiary" size="small" text={ob.skip(lang)} onClick={skip} />
            </div>
        </BaseModal>
    )
}
